import { Loader2, CheckCircle2, Circle } from "lucide-react";
import { StatusBadge } from "./Badges";
import { fmtDate, fmtNum, firstLine } from "../lib/format";

// columns hide when the middle column is narrow (incl. the 3-column layout from 1440 px with the detail panel)
const TEXT_COL = "min-[1800px]:table-cell";
const CAT_COL = "min-[1180px]:table-cell min-[1440px]:hidden min-[1600px]:table-cell";

const Merchant = ({ tx }) => (
  <div className="min-w-0">
    <div className="truncate font-medium text-slate-900">{tx.ai?.merchant_clean || tx.counterparty_raw || firstLine(tx.booking_text)}</div>
    {tx.ai?.merchant_clean && <div className="truncate text-xs text-slate-500">{tx.counterparty_raw}</div>}
    <div className="font-num flex flex-wrap gap-x-2 text-xs text-slate-500 sm:hidden">
      <span>{fmtDate(tx.value_date)}</span>
      <span className={`whitespace-nowrap text-[13px] font-semibold ${tx.amount > 0 ? "text-emerald-700" : "text-slate-900"}`} data-testid="tx-amount-mobile">{tx.amount > 0 ? "+" : ""}{fmtNum(tx.amount)} €</span>
    </div>
  </div>
);

const Category = ({ tx }) => {
  if (tx.ai_status === "done") return <span className="text-sm text-slate-700">{tx.ai?.category}</span>;
  if (tx.ai_status === "failed") return <span className="text-xs text-red-700">Fehler</span>;
  return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
};

const Row = ({ tx, active, onSelect }) => (
  <tr
    data-testid={`transaction-row-${tx.id}`}
    onClick={() => onSelect(tx.id)}
    className={`cursor-pointer border-b border-slate-100 transition-colors duration-100 ${active ? "bg-amber-50/70 shadow-[inset_3px_0_0_#0F172A]" : "hover:bg-slate-50"}`}
  >
    <td className="w-8 py-3 pl-3" data-testid={`tx-check-${tx.id}`} data-done={tx.status !== "offen"}>
      {tx.status !== "offen" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-slate-300" />}
    </td>
    <td className="font-num hidden whitespace-nowrap px-3 py-3 text-sm text-slate-700 sm:table-cell" data-testid="tx-date">{fmtDate(tx.value_date)}</td>
    <td className="max-w-[190px] px-3 py-3 text-[15px] sm:max-w-[260px]"><Merchant tx={tx} /></td>
    <td className={`hidden max-w-[260px] truncate px-3 py-3 text-sm text-slate-500 ${TEXT_COL}`}>{firstLine(tx.booking_text)}</td>
    <td
      data-testid="tx-amount"
      className={`font-num hidden whitespace-nowrap px-3 py-3 text-right sm:table-cell text-[15px] font-semibold ${tx.amount > 0 ? "text-emerald-700" : "text-slate-900"}`}
    >
      {tx.amount > 0 ? "+" : ""}{fmtNum(tx.amount)} €
    </td>
    <td className={`hidden px-3 py-3 ${CAT_COL}`}><Category tx={tx} /></td>
    <td className="px-3 py-3">
      <div className="flex items-center gap-1.5">
        <StatusBadge status={tx.status} testId={`transaction-status-badge-${tx.id}`} />
      </div>
    </td>
  </tr>
);

export const TransactionTable = ({ transactions, selectedId, onSelect }) => {
  if (!transactions.length)
    return (
      <div data-testid="transactions-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
        Keine Buchungen für diese Filter.
      </div>
    );
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="transaction-table">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <th className="w-8 py-2.5 pl-3"><span className="sr-only">Erledigt</span></th>
            <th className="hidden px-3 py-2.5 sm:table-cell">Datum</th>
            <th className="px-3 py-2.5">Empfänger / Händler</th>
            <th className={`hidden px-3 py-2.5 ${TEXT_COL}`}>Text</th>
            <th className="hidden px-3 py-2.5 text-right sm:table-cell">Betrag</th>
            <th className={`hidden px-3 py-2.5 ${CAT_COL}`}>KI-Kategorie</th>
            <th className="px-3 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => <Row key={tx.id} tx={tx} active={tx.id === selectedId} onSelect={onSelect} />)}
        </tbody>
      </table>
    </div>
  );
};
